/* HumanFood bucket gift v2.1
   Auto-adds the recipe-matched bucket when a bag is in the cart.
   Rule: bag SKU -B- maps to pail SKU -P- (HF-CR1-B-11 -> HF-CR1-P-11).
   v2.0: variant map is hardcoded (Shopify drops custom-liquid sections
   from the footer group, so no Liquid context is available). Draft or
   unavailable pails fail the add gracefully and are skipped for the
   rest of the session, so this script is inert until pails go ACTIVE.
   v2.1: (a) re-checks the cart immediately before adding to close a
   cross-page race that could double-add; (b) clamps any gift pail line
   with quantity > 1 back down to 1 (self-healing); (c) pricing note:
   the pail is struck to free by the automatic discounts titled
   "Free bucket with your first bag (11lb/22lb)" (amount-off, applied
   once per order), NOT BxGy - BxGy cannot apply to subscription carts.
   Behaviors:
   - bag added -> matching bucket auto-added (tagged _hfc_gift)
   - bag removed -> its auto-added gift bucket is removed too
   - deliberately purchased buckets (no tag) are never touched
   - never adds a duplicate bucket; heals accidental qty > 1 gifts */
(() => {
  const MAP = {
    'HF-CR1-P-11': 48961868333307,
    'HF-CR1-P-22': 48961873838331,
    'HF-LR1-P-11': 48961873117435,
    'HF-LR1-P-22': 48961875378427,
    'HF-SR1-P-11': 48961869021435,
    'HF-SR1-P-22': 48961874133243
  };

  let busy = false;
  let timer = null;
  const disabledSkus = new Set();

  const bagToPail = (sku) => {
    if (!sku || sku.indexOf('-B-') === -1) return null;
    return sku.replace('-B-', '-P-');
  };

  const pailToBag = (sku) => {
    if (!sku || sku.indexOf('-P-') === -1) return null;
    return sku.replace('-P-', '-B-');
  };

  const getCart = () => {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  };

  const addPail = (sku, variantId) => {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _hfc_gift: 'yes' } }]
      })
    })
      .then((r) => {
        if (!r.ok) disabledSkus.add(sku);
        return r.ok;
      })
      .catch(() => {
        disabledSkus.add(sku);
        return false;
      });
  };

  const removeLine = (lineKey) => {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).catch(() => null);
  };

  const setLineQty = (lineKey, qty) => {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: qty })
    }).catch(() => null);
  };

  const scanCart = (cart) => {
    const bagSkus = new Set();
    const pailItems = [];
    cart.items.forEach((item) => {
      const sku = item.sku || '';
      if (sku.indexOf('-B-') !== -1) bagSkus.add(sku);
      if (MAP[sku] !== undefined) pailItems.push(item);
    });
    return { bagSkus, pailItems };
  };

  const reconcile = async () => {
    if (busy) return;
    busy = true;

    try {
      let cart = await getCart();
      if (!cart || !cart.items) return;

      let { bagSkus, pailItems } = scanCart(cart);

      let changed = false;

      // 1. Remove orphaned gift pails; clamp gift pails stuck at qty > 1.
      for (const item of pailItems) {
        const isGift = item.properties && item.properties._hfc_gift === 'yes';
        if (!isGift) continue;
        const bagSku = pailToBag(item.sku);
        if (bagSku && !bagSkus.has(bagSku)) {
          await removeLine(item.key);
          changed = true;
        } else if (item.quantity > 1) {
          await setLineQty(item.key, 1);
          changed = true;
        }
      }

      // 2. Work out which pails are needed.
      const neededPails = new Set();
      bagSkus.forEach((bagSku) => {
        const pailSku = bagToPail(bagSku);
        if (pailSku && MAP[pailSku] !== undefined && !disabledSkus.has(pailSku)) {
          neededPails.add(pailSku);
        }
      });

      const presentPailSkus = new Set(pailItems.map((i) => i.sku));
      const toAdd = [...neededPails].filter((s) => !presentPailSkus.has(s));

      // 3. Re-check the live cart right before adding (closes the
      //    cross-page race where an add was already in flight).
      if (toAdd.length) {
        cart = await getCart();
        if (!cart || !cart.items) return;
        const fresh = scanCart(cart);
        const freshPresent = new Set(fresh.pailItems.map((i) => i.sku));
        for (const pailSku of toAdd) {
          if (freshPresent.has(pailSku) || !fresh.bagSkus.has(pailToBag(pailSku))) continue;
          const ok = await addPail(pailSku, MAP[pailSku]);
          if (ok) changed = true;
        }
      }

      if (changed && window.location.pathname.indexOf('/cart') === 0) {
        window.location.reload();
      }
    } finally {
      busy = false;
    }
  };

  const schedule = () => {
    if (busy) return;
    clearTimeout(timer);
    timer = setTimeout(reconcile, 700);
  };

  const isCartMutation = (url) => {
    const u = String(url || '');
    return (
      u.indexOf('/cart/add') !== -1 ||
      u.indexOf('/cart/change') !== -1 ||
      u.indexOf('/cart/update') !== -1 ||
      u.indexOf('/cart/clear') !== -1
    );
  };

  const origFetch = window.fetch;
  window.fetch = function () {
    const url = arguments[0] && (arguments[0].url || arguments[0]);
    const result = origFetch.apply(this, arguments);
    if (isCartMutation(url)) {
      result.then(schedule).catch(() => {});
    }
    return result;
  };

  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isCartMutation(url)) {
      this.addEventListener('loadend', schedule);
    }
    return origOpen.apply(this, arguments);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
  window.addEventListener('pageshow', schedule);
})();
